import { EventTracker, EventType } from './eventTracker';
import { generateId } from './utils';

/**
 * Definición del embudo de ventas principal
 */
export enum MainFunnel {
  HOMEPAGE = 1,
  PRODUCT_LIST = 2,
  PRODUCT_DETAIL = 3,
  CART = 4,
  CHECKOUT = 5,
  PURCHASE = 6
}

/**
 * Clase para manejar el tracking de páginas y navegación
 */
export class PageTracker {
  public apiUrl: string;
  private eventTracker: EventTracker;
  private routeChangeCallback: Function | null = null;
  private currentPath: string = '';
  
  constructor(apiUrl: string, eventTracker: EventTracker) {
    this.apiUrl = apiUrl;
    this.eventTracker = eventTracker;
    this.currentPath = window.location.pathname;
  }
  
  /**
   * Inicializa el tracking de páginas
   */
  initialize(): void {
    // Trackear la vista inicial
    this.trackPageView();
    
    // Configurar listener para cambios en la historia (SPA navigation)
    this.setupHistoryListener();
  }
  
  /**
   * Trackea explícitamente un cambio de página
   * Útil para frameworks SPA que no disparan eventos de historial
   * @param url URL de la página actual (opcional)
   */
  trackPageChange(url?: string): void {
    const path = url ? new URL(url, window.location.origin).pathname : window.location.pathname;
    
    if (path !== this.currentPath) {
      this.currentPath = path;
      this.trackPageView();
    }
  }
  
  /**
   * Configura un callback para detectar cambios de ruta en frameworks SPA
   * @param callback Función que será llamada cuando cambie la ruta
   */
  setRouteChangeCallback(callback: Function): void {
    this.routeChangeCallback = callback;
  }
  
  /**
   * Trackea una vista de producto
   * @param productInfo Información del producto visualizado
   */
  trackProductView(productInfo: {
    productId: string;
    productName: string;
    price?: number;
    category?: string;
    [key: string]: any;
  }): void {
    // Determinar el paso del embudo para producto
    const funnelStep = MainFunnel.PRODUCT_DETAIL;
    
    this.eventTracker.track(
      EventType.PRODUCT_VIEW,
      productInfo,
      'main',
      funnelStep
    );
  }
  
  /**
   * Trackea una búsqueda
   * @param searchInfo Información de la búsqueda realizada
   */
  trackSearch(searchInfo: {
    query: string;
    resultsCount?: number;
    filters?: Record<string, any>;
  }): void {
    this.eventTracker.track(
      EventType.SEARCH,
      {
        searchId: generateId(),
        ...searchInfo
      }
    );
  }
  
  // Métodos privados
  
  /**
   * Trackea una vista de página y determina su posición en el embudo
   */
  private trackPageView(): void {
    const path = window.location.pathname;
    const query = Object.fromEntries(new URLSearchParams(window.location.search));
    
    // Determinar el paso del embudo según la URL
    const { funnelName, funnelStep } = this.identifyFunnelStep(path);
    
    this.eventTracker.track(
      EventType.PAGE_VIEW,
      {
        path,
        query,
        pageTitle: document.title
      },
      funnelName,
      funnelStep
    );
  }
  
  /**
   * Configura listeners para el historial para SPAs
   */
  private setupHistoryListener(): void {
    // Monitorear cambios con pushState y replaceState
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;
    
    history.pushState = (...args) => {
      originalPushState.apply(history, args);
      this.handleHistoryChange();
    };
    
    history.replaceState = (...args) => {
      originalReplaceState.apply(history, args);
      this.handleHistoryChange();
    };
    
    // Escuchar eventos de popstate
    window.addEventListener('popstate', () => {
      this.handleHistoryChange();
    });
  }
  
  /**
   * Maneja cambios en el historial y ejecuta el callback
   */
  private handleHistoryChange(): void {
    const path = window.location.pathname;
    
    if (path !== this.currentPath) {
      this.currentPath = path;
      this.trackPageView();
      
      // Ejecutar callback si existe
      if (this.routeChangeCallback) {
        this.routeChangeCallback(path);
      }
    }
  }
  
  /**
   * Identifica el paso del embudo basado en la URL
   */
  private identifyFunnelStep(path: string): { funnelName: string, funnelStep: number | undefined } {
    let funnelName = 'main';
    let funnelStep: number | undefined;
    
    // Identificar el paso en el embudo principal
    if (path === '/' || path === '/home') {
      funnelStep = MainFunnel.HOMEPAGE;
    } else if (path === '/products' || path.startsWith('/products/category/') || path.startsWith('/category/')) {
      funnelStep = MainFunnel.PRODUCT_LIST;
    } else if (/^\/products\/[a-zA-Z0-9-]+$/.test(path) || /^\/product\/[a-zA-Z0-9-]+$/.test(path)) {
      funnelStep = MainFunnel.PRODUCT_DETAIL;
    } else if (path === '/cart' || path === '/shopping-cart') {
      funnelStep = MainFunnel.CART;
    } else if (path === '/checkout' || path.startsWith('/checkout/')) {
      funnelStep = MainFunnel.CHECKOUT;
    } else if (path === '/thank-you' || path === '/confirmation' || path === '/order-confirmation') {
      funnelStep = MainFunnel.PURCHASE;
    }
    
    return { funnelName, funnelStep };
  }
} 